import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import moment from 'moment';
import { format } from 'date-fns';

@Injectable({
  providedIn: 'root'
})
export class DateUtilityService {
  /**
   * Format date for API (YYYY-MM-DD)
   */
  formatDateForAPI(date: Date | string | null): string {
    if (!date) {
      return moment().format('YYYY-MM-DD');
    }
    
    if (date instanceof Date) {
      return moment(date).format('YYYY-MM-DD');
    }
    
    return date;
  }

  /**
   * Set default dates in form
   */
  setDefaultDates(form: FormGroup): void {
    const currentDate = moment();
    const newDate = currentDate.format('YYYY-MM-DD');
    
    // Set start date if not provided
    if (!form.get('startDate')?.value) {
      form.get('startDate')?.setValue(newDate);
    }
    
    // Set end date if not provided
    if (!form.get('endDate')?.value) {
      form.get('endDate')?.setValue(newDate);
    }
    
    // Don't set default pickup time - let user choose
    if (!form.get('pickupTime')?.value) {
      form.get('pickupTime')?.setValue('');
    }
  }

  /**
   * Validate date range
   */
  validateDateRange(fromDate: string, toDate: string): boolean {
    const from = moment(fromDate);
    const to = moment(toDate);
    return to.isSameOrAfter(from);
  }

  /**
   * Get current date in API format
   */
  getCurrentDate(): string {
    return moment().format('YYYY-MM-DD');
  }

  /**
   * Get current time
   */
  getCurrentTime(): string {
    return moment().format('HH:mm');
  }

  /**
   * Check if current time is after 8 PM
   */
  isAfter8PM(): boolean {
    return moment().format('HH:mm') > '20:00';
  }

  /**
   * Format date in dd-MMM-yyyy format (e.g., "15-Jan-2024")
   */
  formatDateForDisplay(date: Date | string | null): string {
    if (!date) {
      return '';
    }
    
    if (date instanceof Date) {
      return format(date, 'dd-MMM-yyyy');
    }
    
    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, 'dd-MMM-yyyy');
      }
    }
    
    return '';
  }

  /**
   * Parse date from dd-MMM-yyyy format
   */
  parseDateFromDisplay(dateString: string): Date | null {
    if (!dateString || typeof dateString !== 'string') {
      return null;
    }
    
    try {
      const parsed = moment(dateString, 'DD-MMM-YYYY').toDate();
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch (error) {
      return null;
    }
  }
} 